import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

function normalizeDomain(input?: string | null) {
  if (!input) return null;
  try {
    let d = input.trim();
    d = d.replace(/^https?:\/\//i, "");
    d = d.replace(/^www\./i, "");
    d = d.replace(/\/$/, "");
    return d || null;
  } catch {
    return null;
  }
}

export function useWebsiteScan(params: {
  initialScan?: any;
  domain?: string | null;
  quizSubmissionId?: string | null;
}) {
  const { initialScan, domain, quizSubmissionId } = params;
  const [scan, setScan] = useState<any>(initialScan || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If we already have scan, no need to fetch
    if (scan) return;

    const domainNorm = normalizeDomain(domain || undefined);
    if (!domainNorm && !quizSubmissionId) return;

    let cancelled = false;
    async function fetchScan() {
      try {
        setLoading(true);
        if (quizSubmissionId) {
          const { data, error } = await supabase
            .from('quiz_submissions')
            .select('website_scan')
            .eq('id', quizSubmissionId)
            .single();
          if (!cancelled && !error && data?.website_scan) {
            const val: any = (data as any).website_scan;
            setScan(val?.openGraph ? val : (val?.data || val));
            setLoading(false);
            return;
          }
        }

        if (domainNorm) {
          const { data, error } = await supabase
            .from('quiz_submissions')
            .select('website_scan')
            .eq('domain_input', domainNorm)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (!cancelled && !error && data?.website_scan) {
            const val: any = (data as any).website_scan;
            setScan(val?.openGraph ? val : (val?.data || val));
          }
        }

        // If still no scan in DB, do a fresh one on the fly (non-blocking feel)
        if (!cancelled && !scan && domainNorm) {
          const { data: freshScan } = await supabase.functions.invoke('scan-website', { body: { domain: domainNorm } });
          if (!cancelled && freshScan) {
            const val: any = (freshScan as any).data || freshScan;
            setScan(val);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchScan();
    return () => { cancelled = true; };
  }, [scan, domain, quizSubmissionId]);

  const logoUrl = useMemo(() => {
    return scan?.openGraph?.image || scan?.data?.openGraph?.image || null;
  }, [scan]);

  return { scan, logoUrl, loading };
}
